import styled from 'styled-components';

// Base text component
const BaseText = styled.span`
  font-family: inherit;
  line-height: 1.5;
  color: ${props => props.color || props.theme.colors.text};
  margin: 0;
  
  ${props => props.$bold && `font-weight: 600;`}
  ${props => props.$semibold && `font-weight: 500;`}
  ${props => props.$light && `font-weight: 300;`}
  
  ${props => props.$italic && `font-style: italic;`}
  ${props => props.$underline && `text-decoration: underline;`}
  ${props => props.$strikethrough && `text-decoration: line-through;`}
  
  ${props => props.$center && `text-align: center;`}
  ${props => props.$right && `text-align: right;`}
  ${props => props.$left && `text-align: left;`}
  
  ${props => props.$uppercase && `text-transform: uppercase;`}
  ${props => props.$lowercase && `text-transform: lowercase;`}
  ${props => props.$capitalize && `text-transform: capitalize;`}
  
  ${props => props.$truncate && `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
  
  ${props => props.$noWrap && `white-space: nowrap;`}
  
  ${props => props.$clickable && `
    cursor: pointer;
    
    &:hover {
      opacity: 0.8;
    }
  `}
`;

// Heading components
export const H1 = styled(BaseText).attrs({ as: 'h1' })`
  font-size: ${props => props.$size || '2.5rem'};
  font-weight: ${props => props.$weight || '700'};
  line-height: 1.2;
  margin-bottom: ${props => props.$marginBottom || '1rem'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.$mobileSize || '2rem'};
  }
`;

export const H2 = styled(BaseText).attrs({ as: 'h2' })`
  font-size: ${props => props.$size || '2rem'};
  font-weight: ${props => props.$weight || '600'};
  line-height: 1.3;
  margin-bottom: ${props => props.$marginBottom || '0.875rem'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.$mobileSize || '1.75rem'};
  }
`;

export const H3 = styled(BaseText).attrs({ as: 'h3' })`
  font-size: ${props => props.$size || '1.5rem'};
  font-weight: ${props => props.$weight || '600'};
  line-height: 1.3;
  margin-bottom: ${props => props.$marginBottom || '0.75rem'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.$mobileSize || '1.375rem'};
  }
`;

export const H4 = styled(BaseText).attrs({ as: 'h4' })`
  font-size: ${props => props.$size || '1.25rem'};
  font-weight: ${props => props.$weight || '600'};
  line-height: 1.4;
  margin-bottom: ${props => props.$marginBottom || '0.625rem'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.$mobileSize || props.$size || '1.125rem'};
  }
`;

export const H5 = styled(BaseText).attrs({ as: 'h5' })`
  font-size: ${props => props.$size || '1.125rem'};
  font-weight: ${props => props.$weight || '600'};
  line-height: 1.4;
  margin-bottom: ${props => props.$marginBottom || '0.5rem'};
`;

export const H6 = styled(BaseText).attrs({ as: 'h6' })`
  font-size: ${props => props.$size || '1rem'};
  font-weight: ${props => props.$weight || '600'};
  line-height: 1.4;
  margin-bottom: ${props => props.$marginBottom || '0.5rem'};
`;

// Body text components
export const Text = styled(BaseText)`
  font-size: ${props => props.$size || '1rem'};
  font-weight: ${props => props.$weight || '400'};
  
  ${props => props.$muted && `
    color: ${props.theme.colors.textSecondary};
  `}
  
  ${props => props.$small && `
    font-size: 0.875rem;
  `}
  
  ${props => props.$large && `
    font-size: 1.125rem;
  `}
`;

export const Paragraph = styled(BaseText).attrs({ as: 'p' })`
  font-size: ${props => props.$size || '1rem'};
  font-weight: ${props => props.$weight || '400'};
  line-height: 1.6;
  margin-bottom: 1rem;
  
  ${props => props.$lead && `
    font-size: 1.125rem;
    font-weight: 300;
    line-height: 1.7;
  `}
  
  ${props => props.$muted && `
    color: ${props.theme.colors.textSecondary};
  `}
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const Small = styled(BaseText).attrs({ as: 'small' })`
  font-size: 0.875rem;
  color: ${props => props.color || props.theme.colors.textSecondary};
`;

export const Caption = styled(BaseText)`
  font-size: 0.75rem;
  color: ${props => props.color || props.theme.colors.textSecondary};
  line-height: 1.4;
`;

export const Label = styled(BaseText).attrs({ as: 'label' })`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.color || props.theme.colors.text};
  display: block;
  margin-bottom: 0.5rem;
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${props.theme.colors.error};
    }
  `}
`;

// Specialized text components
export const Link = styled(BaseText).attrs({ as: 'a' })`
  color: ${props => props.color || props.theme.colors.primary};
  text-decoration: ${props => props.underline ? 'underline' : 'none'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.hoverColor || props.theme.colors.primaryDark};
    text-decoration: underline;
  }
  
  &:active {
    opacity: 0.8;
  }
`;

export const Code = styled(BaseText).attrs({ as: 'code' })`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875em;
  background: ${props => props.theme.colors.bg2};
  color: ${props => props.theme.colors.primary};
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => props.theme.colors.border};
`;

export const Pre = styled.pre`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  background: ${props => props.theme.colors.bg2};
  color: ${props => props.theme.colors.text};
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  overflow-x: auto;
  margin: 1rem 0;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.bg3};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;
  }
`;

export const Blockquote = styled.blockquote`
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.bg2};
  font-style: italic;
  color: ${props => props.theme.colors.textSecondary};
  
  p {
    margin: 0;
  }
  
  cite {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    font-style: normal;
    color: ${props => props.theme.colors.textSecondary};
    
    &:before {
      content: '— ';
    }
  }
`;

// List components
export const List = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ${props => props.unstyled && `
    list-style: none;
    padding-left: 0;
  `}
  
  ${props => props.inline && `
    display: flex;
    gap: 1rem;
    padding-left: 0;
    list-style: none;
    
    li {
      margin-bottom: 0;
    }
  `}
`;

export const OrderedList = styled(List).attrs({ as: 'ol' })``;

// Utility text components
export const Highlight = styled(BaseText)`
  background: ${props => props.color || props.theme.colors.warning + '40'};
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
`;

export const Badge = styled(BaseText)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: ${props => {
    switch (props.variant) {
      case 'success': return props.theme.colors.success;
      case 'error': return props.theme.colors.error;
      case 'warning': return props.theme.colors.warning;
      case 'info': return props.theme.colors.info;
      default: return props.theme.colors.primary;
    }
  }};
  color: white;
  white-space: nowrap;
  
  ${props => props.outline && `
    background: transparent;
    border: 1px solid ${props.theme.colors.primary};
    color: ${props.theme.colors.primary};
  `}
`;

export const TextGradient = styled(BaseText)`
  background: linear-gradient(
    45deg,
    ${props => props.from || props.theme.colors.primary},
    ${props => props.to || props.theme.colors.secondary || props.theme.colors.primaryDark}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${props => props.weight || '600'};
`;
